import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";
import "./SubAdmins.css";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const ACTIONS = [
  { value: "", label: "All actions" },
  { value: "activation.approve", label: "Package approved" },
  { value: "activation.direct", label: "Package activated directly" },
  { value: "activation.reject", label: "Request rejected" },
  { value: "reward", label: "Rewards settled" },
  { value: "id_settlement", label: "ID settlements" },
  { value: "kyc", label: "KYC decisions" },
  { value: "sub_admin", label: "Sub-admin changes" },
];
const ACTION_LABEL = {
  "activation.approve": "Approved",
  "activation.direct": "Activated directly",
  "activation.reject": "Rejected",
  "reward.cash": "Reward paid as cash",
  "reward.item": "Reward item given",
  "id_settlement.settle": "ID settled",
  "id_settlement.paid": "Settlement paid",
  "kyc.approve": "KYC approved",
  "kyc.reject": "KYC rejected",
  "kyc.edit": "KYC details edited",
  "sub_admin.create": "Sub-admin created",
  "sub_admin.update": "Sub-admin updated",
  "old_users.restore": "Old user restored",
  "power.give": "Power given",
  "power.remove": "Power removed",
  "activation.undo": "Activation taken back",
};
const EMPTY = { action: "", actor: "", search: "", from: "", to: "" };

/** One line describing what changed. */
const describe = (r) => {
  const d = r.details || {};
  if (r.action === "activation.reject") return `${d.package || ""} · reason: ${d.reason || "—"}`;
  if (r.action.startsWith("activation.")) {
    const parts = [d.package, d.order_Id ? `order #${d.order_Id}` : null, d.activation_date ? `from ${moment(d.activation_date).format("DD MMM YYYY")}` : null, d.utr ? `UTR ${d.utr}` : null];
    return parts.filter(Boolean).join(" · ");
  }
  if (r.action === "id_settlement.settle") return [`investment ${inr(d.investment)} − income ${inr(d.income)}`, d.calculated !== r.amount ? `calculated ${inr(d.calculated)}` : null, d.note ? `“${d.note}”` : null].filter(Boolean).join(" · ");
  if (r.action === "kyc.edit") return [d.kyc, d.previous_number && d.previous_number !== d.number ? `${d.previous_number} → ${d.number}` : d.number, d.images_replaced ? `${d.images_replaced} image${d.images_replaced > 1 ? "s" : ""} replaced` : null].filter(Boolean).join(" · ");
  if (r.action.startsWith("kyc.")) return [d.kyc, d.number, d.reason ? `reason: ${d.reason}` : null].filter(Boolean).join(" · ");
  if (r.action === "id_settlement.paid") return [d.utr ? `UTR ${d.utr}` : null, d.note ? `“${d.note}”` : null].filter(Boolean).join(" · ");
  if (r.action.startsWith("reward.")) return [d.reward, d.item, d.note ? `“${d.note}”` : null].filter(Boolean).join(" · ");
  if (r.action === "power.give") return d.note ? `“${d.note}”` : "";
  if (r.action === "power.remove") return d.reason ? `reason: ${d.reason}` : "";
  if (r.action === "activation.undo") return [d.reason, d.incomes_taken_back ? `incomes taken back ${inr(d.incomes_taken_back)}` : null].filter(Boolean).join(" · ");
  if (r.action === "old_users.restore") return (d.with_sponsors || []).length ? `with sponsors ${d.with_sponsors.join(", ")}` : "";
  if (r.action === "sub_admin.create") return `@${d.username} · limit ${inr(d.monthly_limit)} · ${(d.modules || []).length} pages`;
  if (r.action === "sub_admin.update") {
    const changes = [];
    const b = d.before || {};
    const a = d.after || {};
    if (b.monthly_limit !== a.monthly_limit) changes.push(`limit ${inr(b.monthly_limit)} → ${inr(a.monthly_limit)}`);
    if (b.status !== a.status) changes.push(a.status === 1 ? "enabled" : "disabled");
    if (JSON.stringify(b.modules) !== JSON.stringify(a.modules)) changes.push("pages changed");
    if (b.name !== a.name || b.email !== a.email) changes.push("profile edited");
    if (d.password_changed) changes.push("password reset");
    return `@${d.username} · ${changes.join(", ") || "saved without changes"}`;
  }
  return "";
};

const AuditLog = () => {
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [actors, setActors] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
    const { data, error } = await apiCall("GET", `/audit-logs?${params.toString()}`);
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load the audit log.");
    setRows(data?.data || []);
    setActors(data?.actors || []);
    setPages(data?.pages || 1);
    setTotal(data?.total || 0);
  }, [applied, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Apply text filters shortly after typing stops; selects apply at once
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied(filters);
    }, 350);
    return () => clearTimeout(t);
  }, [filters]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const active = Object.values(applied).filter(Boolean).length;

  return (
    <div className="plan-settings activation-requests-page audit-log-page">
      <div className="ps-header">
        <div>
          <h2>Audit Log</h2>
          <p>Every package approval, rejection and direct activation, and every sub-admin change — who did it and when.</p>
        </div>
        <ExportButtons
          title="Audit Log"
          columns={[
            { header: "When", value: (r) => (r.at ? moment(r.at).format("DD MMM YYYY, hh:mm A") : "") },
            { header: "Done by", value: (r) => r.actor?.username },
            { header: "Role", value: (r) => (r.actor?.role === "manager" ? "Sub-admin" : r.actor?.role === "admin" ? "Admin" : r.actor?.role) },
            { header: "Action", value: (r) => ACTION_LABEL[r.action] || r.action },
            { header: "Member", value: (r) => r.member?.username },
            { header: "Record", value: (r) => (r.target ? `${r.target.type} ${r.target.id}` : "") },
            { header: "Amount", value: (r) => (r.amount ? inr(r.amount) : "") },
            { header: "Details", value: (r) => describe(r) },
            { header: "IP", value: (r) => r.ip },
          ]}
          filters={Object.entries(applied).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(", ")}
          getRows={() =>
            collectPages(async (p, l) => {
              const params = new URLSearchParams({ page: p, limit: l });
              Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
              const { data } = await apiCall("GET", `/audit-logs?${params.toString()}`);
              return { rows: data?.data || [], pages: data?.pages || 1 };
            })
          }
        />
      </div>

      <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
        <div className="ar-panel-title">
          <h3>Activity</h3>
          <span className="ar-count">{total}</span>
          {active > 0 && <span className="ar-filter-badge">{active} filter{active > 1 ? "s" : ""}</span>}
        </div>

        <div className="ar-filters">
          <div className="ar-f-search">
            <Form.Label>Search</Form.Label>
            <Form.Control size="sm" placeholder="Member username or record #" value={filters.search} onChange={set("search")} />
          </div>
          <div>
            <Form.Label>Action</Form.Label>
            <Form.Select size="sm" value={filters.action} onChange={set("action")}>
              {ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </Form.Select>
          </div>
          <div>
            <Form.Label>Done by</Form.Label>
            <Form.Select size="sm" value={filters.actor} onChange={set("actor")}>
              <option value="">Everyone</option>
              {actors.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </Form.Select>
          </div>
          <div>
            <Form.Label>From</Form.Label>
            <Form.Control size="sm" type="date" value={filters.from} onChange={set("from")} />
          </div>
          <div>
            <Form.Label>To</Form.Label>
            <Form.Control size="sm" type="date" value={filters.to} onChange={set("to")} />
          </div>
          <div className="ar-f-reset">
            <Button size="sm" variant="outline-secondary" disabled={!active} onClick={() => setFilters(EMPTY)}>
              Reset
            </Button>
          </div>
        </div>

        <div className="ps-table-wrap">
          <Table className="ps-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Done by</th>
                <th>Action</th>
                <th>Member / record</th>
                <th>Amount</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="ps-empty">{active ? "Nothing matches these filters." : "No activity recorded yet."}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.id}>
                    <td className="ps-small">
                      <div className="ps-strong">{moment(r.at).format("DD MMM YYYY")}</div>
                      <div className="ps-muted">{moment(r.at).format("hh:mm A")}</div>
                    </td>
                    <td>
                      <div className="ps-strong">{r.actor?.username || "—"}</div>
                      <Badge bg={r.actor?.role === "admin" ? "primary" : "info"}>{r.actor?.role === "admin" ? "Admin" : "Sub admin"}</Badge>
                    </td>
                    <td>
                      <span className={`al-action al-action--${r.action.startsWith("reward.") || r.action.startsWith("id_settlement.") || r.action === "kyc.approve" ? "approve" : r.action === "kyc.reject" ? "reject" : r.action.split(".")[r.action.startsWith("sub_admin") ? 0 : 1]}`}>
                        {ACTION_LABEL[r.action] || r.action}
                      </span>
                    </td>
                    <td className="ps-small">
                      {r.member?.username ? <div className="ps-strong">{r.member.username}</div> : null}
                      {r.target?.id ? (
                        <div className="ps-muted">
                          {r.target.type === "activation_request" ? `Request #${r.target.id}` : r.target.type === "reward_achievement" ? `Achievement #${r.target.id}` : r.target.type === "id_settlement" ? `Settlement #${r.target.id}` : r.target.type === "kyc" ? `${String(r.target.id).split(":")[0].toUpperCase()} KYC` : `Sub-admin #${r.target.id}`}
                        </div>
                      ) : null}
                    </td>
                    <td className="ps-strong">{r.amount != null ? inr(r.amount) : "—"}</td>
                    <td className="al-details">
                      {describe(r)}
                      {r.ip && <div>IP {r.ip}</div>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        <div className="ar-pagination">
          <div className="ar-pagination-info">
            Showing <strong>{total ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, total)}</strong> of <strong>{total}</strong>
          </div>
          <div className="ar-pages">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Prev
            </Button>
            <span className="ps-muted ps-small">Page {page} of {pages}</span>
            <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
